import { ArrowRightIcon, CalendarSync } from 'lucide-react';
import {
  ButtonWizard,
  HeadingWizard,
} from '../..';
import { CardWizard } from '../../../layouts';
import { useEffect, useMemo, useState } from 'react';
import { createColumnHelper, getCoreRowModel } from '@tanstack/react-table';
import { useReactTable } from '@tanstack/react-table';
import { extractErrorMessageFromAPI, formatDayJsDate, mapToDropdownOptions } from '../../../utils';
import {
  useGetCronJobLogsQuery,
  useTriggerCronJobMutation,
} from '../../../store/rtkQuery/travelReq';
import { useNotification } from '../../../contexts';
import CommonTravelTable from './CommonTravelTable';
import { Modal, Select, Spin, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useGetAllUsersQuery } from '../../../store';
import { Option, sortOptions } from '../../../pages/TravelRequest/TravelReqDetail';
const columnHelper = createColumnHelper<any>();

export default function CronJobLogs() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [appliedFilters, setAppliedFilters] = useState<{
    page: number;
    pageSize: number;
    sortBy?: 'asc' | 'desc';
    triggeredById?: string;
  }>({
    page: 1,
    pageSize: 25,
    sortBy: 'desc',
  });
  const [showCronModal, setShowCronModal] = useState(false);
  const [cronType, setCronType] = useState('');

  const cronJob = useGetCronJobLogsQuery(appliedFilters);
  const [triggerCronJob, { isLoading: cronJobLoading }] = useTriggerCronJobMutation();
  const userResponse = useGetAllUsersQuery();

  const [total, setTotal] = useState(0);

  // ✅ total count
  useEffect(() => {
    if (cronJob.data && appliedFilters.page === 1) {
      setTotal(cronJob.data?.data?.count ?? 0);
    }
  }, [cronJob.data]);

  // ✅ table data
  const data = useMemo(
    () => cronJob.data?.data?.list || [],
    [cronJob.data]
  );

  const userDropdown = useMemo(() => {
    if (userResponse.data) {
      const users = userResponse.data?.data || [];
      return mapToDropdownOptions(users, ['EmployeeCode', 'FullName'], 'ID');
    }
    return [];
  }, [userResponse.data]);

  const handleTrigger = async () => {
    try {
      const response = await triggerCronJob({ type: cronType }).unwrap();

      showNotification(
        'success',
        'Success',
        response?.message || 'Cron triggered successfully',
      );

      setShowCronModal(false);
      setCronType('');
    } catch (err) {
      showNotification(
        'error',
        'Error',
        extractErrorMessageFromAPI(err) ||
        'Something went wrong. Please try again',
      );
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'rowNumber',
        header: () => '',
        cell: info => info.row.index + 1, // gives 1, 2, 3...
      }),
      columnHelper.accessor('jobName', {
        header: () => 'Job Name',
        cell: info => info.getValue(),
      }),
      columnHelper.accessor('status', {
        header: () => 'Status',
        cell: info => info.getValue(),
      }),
      columnHelper.accessor('startTime', {
        header: () => 'Start Time',
        cell: info =>
          info.getValue()
            ? formatDayJsDate(dayjs(info.getValue()), 'verbose')
            : '-',
      }),
      columnHelper.accessor('endTime', {
        header: () => 'End Time',
        cell: info =>
          info.getValue()
            ? formatDayJsDate(dayjs(info.getValue()), 'verbose')
            : '-',
      }),
      columnHelper.accessor('errorResponse', {
        header: () => 'Error',
        cell: info => {
          const value = info.getValue();
          if (!value) return '-';
          return (
            <Tooltip
              title={
                typeof value === 'object'
                  ? JSON.stringify(value)
                  : value || 'View location on map'
              }
              placement="right"
            >
              {
                typeof value === 'object'
                  ? JSON.stringify(value)
                  : value
              }
            </Tooltip>
          );
        },
      }),
      columnHelper.accessor('createdAt', {
        header: () => 'Created At',
        cell: info =>
          info.getValue()
            ? formatDayJsDate(dayjs(info.getValue()), 'verbose')
            : '-',
      }),
      columnHelper.accessor('triggeredById', {
        header: () => 'Triggered By',
        cell: ({ row }) => row.original.triggeredByUser?.FullName || '-',
      })
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });
  return (
    <div className="p-1.5 flex flex-col gap-2">
      <Spin spinning={cronJobLoading}>
        <CardWizard addOnClass="p-0 pt-2.5">

          {/* Header */}
          <div className="flex items-center justify-between px-4 mb-3">
            <HeadingWizard addOnClass="text-[#6E6893] font-extrabold uppercase tracking-wider text-sm">
              Cron Job Logs
            </HeadingWizard>

            <div className="flex items-center gap-2">

              <Select
                className="w-48"
                value={appliedFilters.triggeredById}
                placeholder="search triggered by"
                options={userDropdown}
                showSearch
                optionFilterProp="label"
                optionLabelProp="label"
                allowClear
                onChange={(value) =>
                  setAppliedFilters(prev => ({
                    ...prev,
                    triggeredById: value ?? null,
                  }))
                }
              />
              <Select
                value={appliedFilters.sortBy}
                onChange={val =>
                  setAppliedFilters(prev => ({
                    ...prev,
                    sortBy: val,
                    page: 1,
                  }))
                }
                placeholder="Sort by"
                className="w-48"
                allowClear
              >
                {sortOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
              <ButtonWizard
                addOnClass="bg-primary-600 h-8 px-4 flex items-center justify-center mb-1"
                addOnTextClass={''}
                buttonText="Trigger Cron"
                onBtnClick={() => setShowCronModal(true)}
                disabled={
                  cronJobLoading
                }
              >
                <CalendarSync className="size-4 stroke-[3px] font-semibold text-white" />
              </ButtonWizard>
              {/* Back Button */}
              <span
                onClick={() => navigate(-1)}
                className="cursor-pointer text-xs px-2 py-2 rounded-md text-[#4649EC] font-semibold hover:opacity-80 hover:underline transition-colors flex items-center gap-1 border border-[#4649EC]"
              >
                <span>Back</span>
                <ArrowRightIcon className="size-3 stroke-[4px] text-[#4649EC]" />
              </span>
            </div>
          </div>

          {/* Table */}
          <CommonTravelTable
            table={table}
            total={total}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
          />
        </CardWizard>
      </Spin>
      <Modal
        open={showCronModal}
        onCancel={() => setShowCronModal(false)}
        footer={null}
        width={400}
        centered
        title="Trigger Cron Job"
      >
        <div className="space-y-4">

          {/* Input Field */}
          <input
            type="text"
            value={cronType}
            onChange={(e) => setCronType(e.target.value)}
            placeholder="Enter type (e.g. DAILY_SO_SAP_SYNC)"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm
                 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          {/* Trigger Button */}
          <button
            onClick={handleTrigger}
            disabled={!cronType}
            className="w-full bg-gray-800 hover:bg-gray-900
                 disabled:bg-gray-300 disabled:cursor-not-allowed
                 px-4 py-2 rounded-md text-sm font-medium text-white"
          >
            Trigger Cron
          </button>

        </div>
      </Modal>
    </div>
  );
};